import type { MetricChartSpec } from '@/components/charts/generic';
import { graphSettings } from '@/config/settings';
import type { WeightDistributionHistogram } from '@/domain/cages';
import formattedBucket from '@/utils/formattedBucket';
import {
    bucketWeightUnit,
    formatNumberByLocale,
    type Locale,
    toBucketWeight,
} from '@/utils/localeFormatters';

/** Matches the bar colour the histogram has always used. */
const BAR_COLOR = graphSettings.graphColors.weightDistribution;
/** Parts the bars in the card colour (no `color`), so the separator holds in both themes. */
const BAR_BORDER = { width: 0.4 };
/** Bars meet edge to edge, so the distribution reads as continuous. */
const BAR_FILL_FRACTION = 1;
const PERCENT_DECIMALS = 1;
/** Measurement counts are whole things, however fractional the stored bins are. */
const COUNT_DECIMALS = 0;
/** Headroom above the tallest bar so it is not clipped by the axis. */
const MAX_Y_HEADROOM = 1;
export const DISTRIBUTION_ANIMATION_DURATION = 750;

/**
 * The unit the bucket bounds end up in once upscaled — grams for both `g` and
 * `kg`. Callers translate this, not the raw filter tag, or a cage set to `kg`
 * would label gram values as kilograms.
 */
export function resolveBucketWeightUnit(weightUnitTag: string): string {
    return bucketWeightUnit(weightUnitTag) ?? weightUnitTag;
}

export interface WeightDistributionChartSpecInput {
    histogram: WeightDistributionHistogram[];
    /** Unit the bucket bounds are expressed in. */
    weightUnitTag: string;
    /** Already translated: the single series' name, shown in tooltips. */
    seriesLabel: string;
    /** Already translated: the y axis title. */
    axisLabel: string;
    /** Already translated weight unit, appended to each bucket label. */
    formattedWeightUnit: string;
    /** Already translated: label for a bucket's own measurement count. */
    countLabel: string;
    locale: Locale;
    /** `0` disables animation. */
    animationDuration: number;
}

/**
 * Highest bar plus a little headroom, or `0` for an empty histogram — which
 * leaves Chart.js to pick its own bounds rather than collapsing the axis.
 */
export function calculateHistogramMaxY(histogram: WeightDistributionHistogram[]): number {
    if (!histogram?.length) {
        return 0;
    }

    return Math.max(...histogram.map((item) => item.cntPct)) + MAX_Y_HEADROOM;
}

/**
 * A measurement count as the user should read it: whole, and grouped, because an
 * aggregated count runs to six figures.
 */
export function formatCount(count: number, locale: Locale): string {
    return formatNumberByLocale(Math.round(count), locale, COUNT_DECIMALS);
}

/** Ascending by bucket bounds — the order the bars are drawn in. */
function sortByBucket(histogram: WeightDistributionHistogram[]): WeightDistributionHistogram[] {
    return [...(histogram ?? [])].sort(
        (a, b) =>
            (a?.bucket?.from ?? 0) - (b?.bucket?.from ?? 0) ||
            (a?.bucket?.to ?? 0) - (b?.bucket?.to ?? 0)
    );
}

/**
 * Every measurement behind the histogram.
 *
 * Bucket counts are fractional — the 50 g histograms are rebinned to 5 g by a
 * script that redistributes counts across the narrower bins — so this sums the
 * raw doubles and leaves rounding to the caller, who rounds once at the end.
 */
export function totalMeasurementCount(histogram: WeightDistributionHistogram[]): number {
    return (histogram ?? []).reduce((total, item) => total + (item?.cnt ?? 0), 0);
}

/** `120 - 140 g`, with the bounds converted into the requested weight unit. */
function bucketLabel(
    item: WeightDistributionHistogram,
    weightUnitTag: string,
    formattedWeightUnit: string
): string {
    const [label] = formattedBucket({
        from: toBucketWeight(item?.bucket?.from ?? 0, weightUnitTag),
        to: toBucketWeight(item?.bucket?.to ?? 0, weightUnitTag),
        metric: formattedWeightUnit,
    });

    // formattedBucket only returns null for bounds it cannot convert, an unknown unit tag.
    return label ?? '';
}

/**
 * Builds the weight-distribution histogram as a generic bar chart spec.
 *
 * The x axis is categorical rather than temporal — one bucket per bar — so the
 * bucket labels are also the join keys the generic builder matches the series
 * against. They are unique by construction: buckets do not overlap.
 */
export function buildWeightDistributionChartSpec(
    input: WeightDistributionChartSpecInput
): MetricChartSpec<'bar'> {
    const {
        histogram,
        weightUnitTag,
        seriesLabel,
        axisLabel,
        formattedWeightUnit,
        countLabel,
        locale,
        animationDuration,
    } = input;

    const sorted = sortByBucket(histogram);

    const buckets = sorted.map((item) => ({
        key: bucketLabel(item, weightUnitTag, formattedWeightUnit),
        value: item?.cntPct ?? null,
    }));

    return {
        kind: 'bar',
        xAxisKind: 'category',
        // One series, no legend: the swatch names nothing, and its indent pushes
        // the share line out of line with the measurement count below it.
        showTooltipColors: false,
        days: buckets.map((bucket) => bucket.key),
        animationDuration,
        locale,
        series: [
            {
                key: 'share-in-interval',
                label: seriesLabel,
                color: BAR_COLOR,
                barPercentage: BAR_FILL_FRACTION,
                categoryPercentage: BAR_FILL_FRACTION,
                barBorder: BAR_BORDER,
                // A single series needs no legend, and one lone checkbox that
                // cannot be unticked is worse than none.
                showInLegend: false,
                data: buckets.map((bucket) => ({ day: bucket.key, value: bucket.value })),
            },
        ],
        left: {
            label: axisLabel,
            unit: '%',
            beginAtZero: true,
            max: calculateHistogramMaxY(sorted),
            tickDecimals: PERCENT_DECIMALS,
            valueDecimals: PERCENT_DECIMALS,
        },
        tooltipExtraLines: (index: number): string[] => {
            const item = sorted[index];

            if (!item) {
                return [];
            }

            return [`${countLabel}: ${formatCount(item.cnt ?? 0, locale)}`];
        },
    };
}
